import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Car, MapPin, Clock, Shield, Star, Phone } from "@phosphor-icons/react";
import SEO from "@/components/SEO";

const TRANSPORT_SEO = {
  "chauffeur-prive-versailles": {
    title: "Chauffeur Privé à Versailles — Transferts & Mise à Disposition",
    h1: "Chauffeur privé à Versailles",
    description: "Réservez un chauffeur privé à Versailles pour vos transferts vers Paris, CDG, Orly, les gares et vos déplacements professionnels. Mercedes Classe E, Classe V, Classe S. Départ depuis votre domicile, hôtel ou entreprise.",
    intro: "ALTURA met à votre disposition des chauffeurs expérimentés pour tous vos déplacements au départ ou à destination de Versailles. Transferts vers les aéroports, trajets professionnels, soirées et événements — nous intervenons depuis votre domicile, votre hôtel ou votre entreprise.",
    city: "Versailles",
    routes: [
      { label: "Versailles → Aéroport CDG", desc: "Prise en charge à votre adresse versaillaise, autoroute directe.", time: "~1h15", link: "/services/deplacements" },
      { label: "Versailles → Aéroport Orly", desc: "Trajet rapide via l'A86 ou la Francilienne.", time: "~45 min", link: "/services/deplacements" },
      { label: "Versailles → Paris centre", desc: "Rendez-vous, dîner, spectacle — dépose et retour au choix.", time: "~40 min", link: "/services/deplacements" },
      { label: "Versailles → Gare Montparnasse", desc: "Connexion TGV vers l'Ouest et le Sud.", time: "~35 min", link: "/services/deplacements" },
      { label: "Mise à disposition Versailles", desc: "Chauffeur à votre disposition pour la demi-journée ou la journée.", time: "Sur mesure", link: "/services/deplacements" },
    ],
    communes: ["Le Chesnay-Rocquencourt", "Bailly", "Noisy-le-Roi", "Saint-Nom-la-Bretèche", "Buc", "Viroflay", "Vélizy-Villacoublay"],
    note: "Les trajets au départ de Versailles sont distincts des excursions au Château de Versailles depuis Paris. Pour une visite du Château avec chauffeur, consultez notre page Destinations.",
    noteLink: "/destinations",
  },
  "chauffeur-prive-poissy": {
    title: "Chauffeur Privé à Poissy — Transferts & Mise à Disposition",
    h1: "Chauffeur privé à Poissy",
    description: "Chauffeur privé à Poissy pour vos transferts aéroports CDG et Orly, trajets vers Paris et La Défense. Mercedes Classe E, V, S. Prise en charge à domicile, en entreprise ou en gare.",
    intro: "Situés à proximité de Poissy, nos chauffeurs vous rejoignent rapidement pour tous vos trajets. Que vous partiez en voyage depuis votre domicile, que vous vous rendiez à La Défense ou dans Paris, ALTURA assure votre transport dans le calme et la ponctualité.",
    city: "Poissy",
    routes: [
      { label: "Poissy → Aéroport CDG", desc: "Trajet direct via l'A14 et l'A1.", time: "~50 min", link: "/services/deplacements" },
      { label: "Poissy → Aéroport Orly", desc: "Par l'A13 et l'A86.", time: "~55 min", link: "/services/deplacements" },
      { label: "Poissy → La Défense", desc: "Accès rapide au quartier d'affaires.", time: "~25 min", link: "/services/deplacements" },
      { label: "Poissy → Paris centre", desc: "Via l'A14, dépose à l'adresse de votre choix.", time: "~40 min", link: "/services/deplacements" },
      { label: "Mise à disposition Poissy", desc: "Chauffeur dédié pour vos déplacements de la journée.", time: "Sur mesure", link: "/services/deplacements" },
    ],
    communes: ["Orgeval", "Villennes-sur-Seine", "Médan", "Chambourcy", "Aigremont", "Morainvilliers"],
    note: null,
    noteLink: null,
  },
  "chauffeur-prive-saint-germain-en-laye": {
    title: "Chauffeur Privé à Saint-Germain-en-Laye — Transferts & Déplacements",
    h1: "Chauffeur privé à Saint-Germain-en-Laye",
    description: "Chauffeur privé à Saint-Germain-en-Laye et communes voisines. Transferts aéroports, trajets vers Paris, La Défense, gares. Le Vésinet, Maisons-Laffitte, Chatou, Croissy-sur-Seine.",
    intro: "ALTURA dessert Saint-Germain-en-Laye et l'ensemble des communes résidentielles voisines. Nos chauffeurs viennent vous chercher à votre domicile pour des transferts aéroports, des trajets vers Paris ou des mises à disposition pour vos événements.",
    city: "Saint-Germain-en-Laye",
    routes: [
      { label: "Saint-Germain → Aéroport CDG", desc: "Via l'A14 et l'A1, prise en charge à domicile.", time: "~55 min", link: "/services/deplacements" },
      { label: "Saint-Germain → Aéroport Orly", desc: "Par l'A13 et l'A86.", time: "~50 min", link: "/services/deplacements" },
      { label: "Saint-Germain → Paris centre", desc: "Pour vos rendez-vous, dîners ou spectacles.", time: "~35 min", link: "/services/deplacements" },
      { label: "Saint-Germain → La Défense", desc: "Accès direct au quartier d'affaires.", time: "~20 min", link: "/services/deplacements" },
      { label: "Mise à disposition", desc: "Chauffeur dédié à la demi-journée ou à la journée.", time: "Sur mesure", link: "/services/deplacements" },
    ],
    communes: ["Le Vésinet", "Maisons-Laffitte", "Croissy-sur-Seine", "Chatou", "Marly-le-Roi", "Louveciennes", "Le Pecq", "Fourqueux"],
    note: null,
    noteLink: null,
  },
};

export default function TransportSEOPage() {
  const navigate = useNavigate();
  const pathname = window.location.pathname;
  const slug = pathname.split("/services/")[1] || "";
  const page = TRANSPORT_SEO[slug];

  if (!page) return null; // Let React Router handle 404

  return (
    <>
      <SEO title={page.title} description={page.description} />

      {/* Hero */}
      <section className="bg-[#1A1A1A] py-20 sm:py-28" data-testid={`seo-transport-${slug}`}>
        <div className="max-w-4xl mx-auto px-6">
          <button onClick={() => navigate("/services")} className="text-white/40 text-xs flex items-center gap-2 mb-8 hover:text-white/70">
            <ArrowLeft size={12} /> Services
          </button>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-white/30 text-xs tracking-[0.3em] uppercase mb-4">
            Transport privé — {page.city}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide mb-5">
            {page.h1}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="text-white/50 text-sm sm:text-base max-w-2xl leading-relaxed">
            {page.intro}
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="flex flex-wrap gap-3 mt-8">
            <Link to="/services/deplacements"
              className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] px-7 py-3 text-xs tracking-[0.15em] uppercase hover:bg-[#EDEBE4] transition-colors"
              data-testid="seo-transport-cta">
              Réserver un chauffeur <ArrowRight size={14} weight="bold" />
            </Link>
            <a href="tel:+33681969342"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-3 text-xs tracking-[0.15em] uppercase hover:border-white/50 transition-colors">
              <Phone size={14} /> Appeler
            </a>
          </motion.div>
        </div>
      </section>

      {/* Routes / Trajets */}
      <section className="bg-[#EDEBE4] py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading text-xl sm:text-2xl text-[#1A1A1A] mb-8">Trajets populaires depuis {page.city}</h2>
          <div className="space-y-3">
            {page.routes.map((r, i) => (
              <motion.div key={r.label} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <Link to={r.link} className="flex items-center justify-between bg-white border border-[#1A1A1A]/10 p-5 hover:border-[#1A1A1A]/30 transition-colors group">
                  <div>
                    <p className="text-[#1A1A1A] text-sm font-medium">{r.label}</p>
                    <p className="text-[#1A1A1A]/50 text-xs mt-0.5">{r.desc}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    <span className="text-[#1A1A1A]/40 text-xs"><Clock size={12} className="inline mr-1" />{r.time}</span>
                    <ArrowRight size={14} className="text-[#1A1A1A]/20 group-hover:text-[#1A1A1A] transition-colors" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          {page.note && (
            <div className="mt-6 bg-white border border-[#1A1A1A]/10 p-4">
              <p className="text-[#1A1A1A]/60 text-xs leading-relaxed">{page.note}{" "}
                {page.noteLink && <Link to={page.noteLink} className="underline text-[#1A1A1A]/80">Voir les destinations</Link>}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Fleet */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading text-xl sm:text-2xl text-[#1A1A1A] mb-8 text-center">Notre flotte</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { name: "Mercedes Classe E", desc: "Berline élégante, 3 passagers.", ideal: "Transferts aéroports, rendez-vous." },
              { name: "Mercedes Classe V", desc: "Van premium, jusqu'à 7 passagers.", ideal: "Familles, groupes, bagages volumineux." },
              { name: "Mercedes Classe S", desc: "Berline de prestige.", ideal: "Événements, soirées, VIP." },
            ].map(v => (
              <div key={v.name} className="border border-[#1A1A1A]/10 p-5">
                <Car size={24} weight="light" className="text-[#1A1A1A]/40 mb-3" />
                <h3 className="text-[#1A1A1A] text-sm font-medium mb-1">{v.name}</h3>
                <p className="text-[#1A1A1A]/50 text-xs leading-relaxed">{v.desc}</p>
                <p className="text-[#1A1A1A]/40 text-xs mt-1">{v.ideal}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Communes desservies */}
      <section className="bg-[#EDEBE4] py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-xl sm:text-2xl text-[#1A1A1A] mb-3">Secteurs desservis</h2>
          <p className="text-[#1A1A1A]/50 text-sm mb-8">Nos chauffeurs interviennent à {page.city} et dans les communes voisines.</p>
          <div className="flex flex-wrap justify-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1A1A1A] text-white text-xs"><MapPin size={10} weight="fill" /> {page.city}</span>
            {page.communes.map(c => (
              <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#1A1A1A]/10 text-[#1A1A1A]/70 text-xs">
                <MapPin size={10} weight="fill" className="text-[#1A1A1A]/30" /> {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Trust + CTA */}
      <section className="bg-[#1A1A1A] py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid sm:grid-cols-3 gap-6 text-center mb-10">
            {[
              { icon: Shield, label: "Chauffeurs vérifiés", desc: "Formés, ponctuels et discrets" },
              { icon: Clock, label: "Disponibilité 24h/7j", desc: "Vols tôt le matin ou tard le soir" },
              { icon: Star, label: "Flotte Mercedes", desc: "Classe E, Classe V, Classe S" },
            ].map(s => (
              <div key={s.label}>
                <s.icon size={24} weight="light" className="text-white/40 mx-auto mb-2" />
                <p className="text-white text-sm font-medium mb-1">{s.label}</p>
                <p className="text-white/40 text-xs">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/services/deplacements"
              className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] px-8 py-3 text-xs tracking-[0.15em] uppercase hover:bg-[#EDEBE4] transition-colors">
              Réserver un chauffeur à {page.city} <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

// Export the slugs for the router
export const TRANSPORT_SEO_SLUGS = Object.keys(TRANSPORT_SEO);
